/**
 * DirectiveInput - Direktif input kutusu
 *
 * Kullanici projeye yeni bir yon verebilir.
 * Direktif tipleri: direction_change, question, resource, accelerate, stop, branch
 */

import { Loader, Send } from 'lucide-react'
import { useState } from 'react'

interface DirectiveInputProps {
  projectId: number
  onDirectiveSent: () => void
}

export default function DirectiveInput({ projectId, onDirectiveSent }: DirectiveInputProps) {
  const [directive, setDirective] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSend = async () => {
    if (!directive.trim()) {
      return
    }

    try {
      setSending(true)
      setError(null)

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "http://localhost:8888"}/api/projects/${projectId}/directive`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: directive })
        }
      )

      if (!response.ok) {
        throw new Error('Direktif gonderilemedi')
      }

      setDirective('')
      onDirectiveSent()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bilinmeyen hata')
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      void handleSend()
    }
  }

  return (
    <div className="space-y-3">
      {/* Input */}
      <textarea
        value={directive}
        onChange={(e) => setDirective(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Projeye yeni bir yon verin... (Ctrl+Enter ile gonder)"
        className="w-full p-3 rounded border"
        style={{
          background: 'var(--input-bg)',
          color: 'var(--text-primary)',
          borderColor: 'var(--border-color)'
        }}
        rows={4}
        disabled={sending}
      />

      {/* Hata mesaji */}
      {error && (
        <div
          className="p-3 rounded text-sm"
          style={{
            background: 'var(--danger-bg)',
            color: 'var(--danger-color)'
          }}
        >
          {error}
        </div>
      )}

      {/* Gonder butonu */}
      <button
        onClick={() => void handleSend()}
        disabled={!directive.trim() || sending}
        className="btn btn-primary w-full flex items-center justify-center gap-2"
      >
        {sending ? (
          <>
            <Loader size={16} className="animate-spin" />
            Gonderiliyor...
          </>
        ) : (
          <>
            <Send size={16} />
            Direktif Gonder
          </>
        )}
      </button>

      {/* Yardim metni */}
      <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
        Ornek: &ldquo;Antimadde yakitina odaklan&rdquo;, &ldquo;Pratik uygulamalari incele&rdquo;
      </p>
    </div>
  )
}
